import { Team, DisplayFixture, GameState } from '../domain/entities';
import { ASSISTANT_MANAGER_LEVELS } from '../domain/constants';

export interface TacticalRecommendation {
    formation: string;
    mentality: 'Defensive' | 'Balanced' | 'Attacking';
    defensiveStyle: 'High Press' | 'Standard' | 'Deep Block';
    passingStyle: 'Direct' | 'Short' | 'Mixed';
    attackingFocus: 'Through Middle' | 'Down Left Flank' | 'Down Right Flank' | 'Mixed';
    tacklingStyle: 'Aggressive' | 'Normal' | 'Cautious';
    reasoning: string;
}

export interface TransferRecommendation {
    playerId: number;
    playerName: string;
    action: 'sell' | 'buy';
    reason: string;
    suggestedPrice?: number;
    position?: string;
    skill?: number;
    age?: number;
}

class AssistantManagerService {
    /**
     * Generate tactical recommendations based on team strengths and opponent analysis
     */
    generateTacticalRecommendation(
        playerTeam: Team,
        nextOpponent: Team | null,
        managerLevel: number
    ): TacticalRecommendation {
        const accuracy = ASSISTANT_MANAGER_LEVELS[managerLevel - 1]?.accuracy || 0.6;

        // Analyze team composition
        const gkCount = playerTeam.squad.filter(p => p.position === 'GK').length;
        const defCount = playerTeam.squad.filter(p => p.position === 'DEF').length;
        const midCount = playerTeam.squad.filter(p => p.position === 'MID').length;
        const fwdCount = playerTeam.squad.filter(p => p.position === 'FWD').length;

        const avgDefSkill = defCount > 0
            ? playerTeam.squad.filter(p => p.position === 'DEF').reduce((sum, p) => sum + p.skill, 0) / defCount
            : 0;
        const avgMidSkill = midCount > 0
            ? playerTeam.squad.filter(p => p.position === 'MID').reduce((sum, p) => sum + p.skill, 0) / midCount
            : 0;
        const avgFwdSkill = fwdCount > 0
            ? playerTeam.squad.filter(p => p.position === 'FWD').reduce((sum, p) => sum + p.skill, 0) / fwdCount
            : 0;

        // Determine formation based on squad balance
        let formation = '4-4-2'; // Default
        if (defCount >= 5 && midCount <= 4) {
            formation = '5-3-2';
        } else if (defCount <= 4 && midCount >= 5) {
            formation = '3-5-2';
        } else if (fwdCount >= 3 && midCount <= 4) {
            // Only use 4-3-3 if you have good forwards AND limited midfielders
            formation = '4-3-3';
        }

        // Determine mentality based on team strength vs opponent
        let mentality: 'Defensive' | 'Balanced' | 'Attacking' = 'Balanced';
        if (nextOpponent) {
            const opponentAvgSkill = nextOpponent.squad.length > 0 ? nextOpponent.squad.reduce((sum, p) => sum + p.skill, 0) / nextOpponent.squad.length : 0;
            const playerAvgSkill = playerTeam.squad.length > 0 ? playerTeam.squad.reduce((sum, p) => sum + p.skill, 0) / playerTeam.squad.length : 0;

            if (playerAvgSkill > opponentAvgSkill + 5) {
                mentality = 'Attacking';
            } else if (playerAvgSkill < opponentAvgSkill - 5) {
                mentality = 'Defensive';
            }
        } else {
            // Use team balance
            if (avgFwdSkill > avgDefSkill + 5) {
                mentality = 'Attacking';
            } else if (avgDefSkill > avgFwdSkill + 5) {
                mentality = 'Defensive';
            }
        }

        // Determine defensive style based on defensive strength
        let defensiveStyle: 'High Press' | 'Standard' | 'Deep Block' = 'Standard';
        if (avgDefSkill > 70) {
            defensiveStyle = 'High Press';
        } else if (avgDefSkill < 55) {
            defensiveStyle = 'Deep Block';
        }

        // Determine passing style based on midfield skill
        let passingStyle: 'Direct' | 'Short' | 'Mixed' = 'Mixed';
        if (avgMidSkill > 70) {
            passingStyle = 'Short';
        } else if (avgMidSkill < 55) {
            passingStyle = 'Direct';
        }

        // Determine attacking focus based on player strengths on each flank
        let attackingFocus: 'Through Middle' | 'Down Left Flank' | 'Down Right Flank' | 'Mixed' = 'Mixed';
        if (avgMidSkill > 65) {
            attackingFocus = 'Through Middle';
        } else {
            // Analyze left vs right flank strength
            const leftFlankPlayers = playerTeam.squad.filter(p =>
                (p.position === 'MID' || p.position === 'DEF' || p.position === 'FWD') && p.favoredFoot === 'Left'
            );
            const rightFlankPlayers = playerTeam.squad.filter(p =>
                (p.position === 'MID' || p.position === 'DEF' || p.position === 'FWD') && p.favoredFoot === 'Right'
            );

            const leftFlankAvgSkill = leftFlankPlayers.length > 0
                ? leftFlankPlayers.reduce((sum, p) => sum + p.skill, 0) / leftFlankPlayers.length
                : 0;
            const rightFlankAvgSkill = rightFlankPlayers.length > 0
                ? rightFlankPlayers.reduce((sum, p) => sum + p.skill, 0) / rightFlankPlayers.length
                : 0;

            if (leftFlankAvgSkill > rightFlankAvgSkill + 3) {
                attackingFocus = 'Down Left Flank';
            } else if (rightFlankAvgSkill > leftFlankAvgSkill + 3) {
                attackingFocus = 'Down Right Flank';
            }
        }

        // Determine tackling style based on team age/cards
        let tacklingStyle: 'Aggressive' | 'Normal' | 'Cautious' = 'Normal';
        const avgAge = playerTeam.squad.length > 0 ? playerTeam.squad.reduce((sum, p) => sum + p.age, 0) / playerTeam.squad.length : 0;
        if (avgAge < 26 && playerTeam.squad.filter(p => p.redCards || p.yellowCards >= 2).length < 3) {
            tacklingStyle = 'Aggressive';
        } else if (playerTeam.squad.filter(p => p.redCards || p.yellowCards >= 3).length > 3) {
            tacklingStyle = 'Cautious';
        }

        // Add randomness based on manager accuracy
        if (Math.random() > accuracy) {
            const options: ('Defensive' | 'Balanced' | 'Attacking')[] = ['Defensive', 'Balanced', 'Attacking'];
            mentality = options[Math.floor(Math.random() * options.length)];
        }

        const reasoning = `Based on team analysis: Using ${formation} formation to leverage your ${defCount} defenders and ${midCount} midfielders. ` +
            `${mentality === 'Attacking' ? 'Your squad is strong enough to attack' : mentality === 'Defensive' ? 'Recommending defensive approach' : 'Balanced approach suits squad composition'}. ` +
            `${defensiveStyle === 'High Press' ? 'Your defense is strong - press aggressively' : 'Compact defensive shape advised'}.`;

        return {
            formation,
            mentality,
            defensiveStyle,
            passingStyle,
            attackingFocus,
            tacklingStyle,
            reasoning
        };
    }

    /**
     * Generate transfer recommendations based on team analysis
     */
    generateTransferRecommendations(
        playerTeam: Team,
        managerLevel: number
    ): TransferRecommendation[] {
        const accuracy = ASSISTANT_MANAGER_LEVELS[managerLevel - 1]?.accuracy || 0.6;
        const recommendations: TransferRecommendation[] = [];

        // Identify weak positions
        const positionAvgs: Record<string, number> = {};
        ['GK', 'DEF', 'MID', 'FWD'].forEach(pos => {
            const players = playerTeam.squad.filter(p => p.position === pos);
            if (players.length > 0) {
                positionAvgs[pos] = players.reduce((sum, p) => sum + p.skill, 0) / players.length;
            }
        });

        // Identify players to sell (old, low skill, low form)
        playerTeam.squad.forEach(player => {
            if (player.age > 32 && player.skill < 60) {
                if (Math.random() < accuracy) {
                    recommendations.push({
                        playerId: player.id,
                        playerName: player.name,
                        action: 'sell',
                        reason: `Age and declining performance suggest it's time to move on`,
                        position: player.position,
                        skill: player.skill,
                        age: player.age
                    });
                }
            } else if (player.form < -3 && player.age > 27) {
                if (Math.random() < accuracy * 0.7) {
                    recommendations.push({
                        playerId: player.id,
                        playerName: player.name,
                        action: 'sell',
                        reason: `Consistent poor form suggests need for fresh option`,
                        position: player.position,
                        skill: player.skill,
                        age: player.age
                    });
                }
            }
        });

        return recommendations.slice(0, 3); // Limit to 3 suggestions
    }
}

export const assistantManagerService = new AssistantManagerService();
